import React, { useState, useEffect } from 'react';
import { Smartphone, Lock, Unlock, Shield, Activity, Clock, CheckCircle, XCircle, Eye, Search, Filter, Loader2 } from 'lucide-react';
import { useDevices } from '../hooks/useDevices';
import { useLockRequests } from '../hooks/useLockRequests';
import { useFinanceStatus } from '../hooks/useFinanceStatus';

const FinanceDashboard: React.FC = () => {
  const { devices, loading: devicesLoading, fetchDevices } = useDevices();
  const { lockRequests, loading: requestsLoading, fetchLockRequests, createLockRequest } = useLockRequests();
  const { financeStatus, refreshStatus } = useFinanceStatus();
  
  const [selectedDevice, setSelectedDevice] = useState<string | null>(null);
  const [showDeviceModal, setShowDeviceModal] = useState(false);
  const [showLockRequestModal, setShowLockRequestModal] = useState(false);
  const [requestType, setRequestType] = useState<'LOCK' | 'UNLOCK'>('LOCK');
  const [requestReason, setRequestReason] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'LOCKED' | 'UNLOCKED'>('ALL');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    refreshStatus();
    fetchDevices();
    fetchLockRequests(true); // Only get current user's requests
  }, []);

  const handleDeviceClick = (deviceId: string) => {
    setSelectedDevice(deviceId);
    setShowDeviceModal(true);
  };

  const handleLockRequest = (deviceId: string, type: 'LOCK' | 'UNLOCK') => {
    setSelectedDevice(deviceId);
    setRequestType(type);
    setShowLockRequestModal(true);
  };

  const submitLockRequest = async () => {
    if (!selectedDevice || !requestReason.trim()) return;

    setIsSubmitting(true);
    try {
      await createLockRequest(selectedDevice, requestType, requestReason);
      setShowLockRequestModal(false);
      setRequestReason('');
      setSelectedDevice(null);
      // Refresh the requests list
      await fetchLockRequests(true);
    } catch (error) {
      console.error('Error submitting lock request:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredDevices = devices.filter(device => {
    const matchesSearch = device.device_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         device.owner_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         device.serial_number.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'ALL' || 
                         (statusFilter === 'LOCKED' && device.is_locked) ||
                         (statusFilter === 'UNLOCKED' && !device.is_locked);
    
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'PENDING':
        return (
          <div className="inline-flex items-center space-x-1 bg-yellow-500/20 text-yellow-600 dark:text-yellow-400 px-2 py-1 rounded-full border border-yellow-500/30">
            <Clock className="w-3 h-3" />
            <span className="text-xs font-medium">PENDING</span>
          </div>
        );
      case 'APPROVED':
        return (
          <div className="inline-flex items-center space-x-1 bg-green-500/20 text-green-600 dark:text-green-400 px-2 py-1 rounded-full border border-green-500/30">
            <CheckCircle className="w-3 h-3" />
            <span className="text-xs font-medium">APPROVED</span>
          </div>
        );
      case 'DENIED':
        return (
          <div className="inline-flex items-center space-x-1 bg-red-500/20 text-red-600 dark:text-red-400 px-2 py-1 rounded-full border border-red-500/30">
            <XCircle className="w-3 h-3" />
            <span className="text-xs font-medium">DENIED</span>
          </div>
        );
      default:
        return (
          <div className="inline-flex items-center space-x-1 bg-gray-500/20 text-gray-600 dark:text-gray-400 px-2 py-1 rounded-full border border-gray-500/30">
            <span className="text-xs font-medium">UNKNOWN</span>
          </div>
        );
    }
  };

  if (financeStatus !== 'APPROVED') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white/10 backdrop-blur-xl shadow-2xl rounded-2xl p-8 text-center border border-slate-700/50">
          <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-yellow-500 to-amber-600 rounded-2xl flex items-center justify-center shadow-xl">
            <Shield className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-4">
            Access Pending
          </h2>
          <p className="text-slate-300 mb-6 leading-relaxed">
            Your finance team access is {financeStatus === 'PENDING' ? 'pending approval' : 'not yet registered'}.
          </p>
          <p className="text-sm text-slate-400">
            Please contact an administrator to request access to the finance portal.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-4 mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">
                Finance Portal
              </h1>
              <p className="text-slate-400 mt-1">
                Manage device lock/unlock requests
              </p>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-6 border border-slate-700/50 shadow-2xl hover:border-slate-600/50 transition-all duration-200">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
                <Smartphone className="w-6 h-6 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-slate-400">Total Devices</p>
                <p className="text-2xl font-bold text-white">{devices.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-6 border border-slate-700/50 shadow-2xl hover:border-slate-600/50 transition-all duration-200">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center shadow-lg">
                <Lock className="w-6 h-6 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-slate-400">Locked Devices</p>
                <p className="text-2xl font-bold text-white">
                  {devices.filter(d => d.is_locked).length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-6 border border-slate-700/50 shadow-2xl hover:border-slate-600/50 transition-all duration-200">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-amber-600 rounded-xl flex items-center justify-center shadow-lg">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-slate-400">Pending Requests</p>
                <p className="text-2xl font-bold text-white">
                  {lockRequests.filter(r => r.status === 'PENDING').length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-6 border border-slate-700/50 shadow-2xl hover:border-slate-600/50 transition-all duration-200">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg">
                <Activity className="w-6 h-6 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-slate-400">My Requests</p>
                <p className="text-2xl font-bold text-white">{lockRequests.length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Device List */}
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl border border-slate-700/50 shadow-2xl">
          <div className="px-6 py-4 border-b border-slate-700/30">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                <Smartphone className="w-4 h-4 text-white" />
              </div>
              <h2 className="text-lg font-semibold text-white">Device Management</h2>
            </div>
            
            {/* Search and Filter */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search devices..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-white/10 border border-slate-600/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 backdrop-blur-sm"
                />
              </div>
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as 'ALL' | 'LOCKED' | 'UNLOCKED')}
                  className="pl-10 pr-8 py-2 bg-white/10 border border-slate-600/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 backdrop-blur-sm appearance-none"
                >
                  <option value="ALL" className="bg-slate-800">All Status</option>
                  <option value="LOCKED" className="bg-slate-800">Locked</option>
                  <option value="UNLOCKED" className="bg-slate-800">Unlocked</option>
                </select>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-slate-700/30">
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Device
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Owner
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/30">
                {devicesLoading ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-8 text-center">
                      <div className="flex items-center justify-center space-x-2">
                        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                        <span className="text-slate-400">Loading devices...</span>
                      </div>
                    </td>
                  </tr>
                ) : filteredDevices.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-8 text-center text-slate-400">
                      No devices found
                    </td>
                  </tr>
                ) : (
                  filteredDevices.map((device) => (
                    <tr key={device.id} className="hover:bg-white/5 transition-colors duration-150">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center shadow-lg">
                              <Smartphone className="w-5 h-5 text-white" />
                            </div>
                          </div>
                          <div className="ml-4">
                            <div 
                              className="text-sm font-medium text-blue-400 hover:text-blue-300 cursor-pointer transition-colors"
                              onClick={() => handleDeviceClick(device.id)}
                            >
                              {device.device_name}
                            </div>
                            <div className="text-sm text-slate-400">
                              {device.serial_number}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                        {device.owner_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full border text-xs font-medium ${
                          device.is_locked 
                            ? 'bg-red-500/20 text-red-400 border-red-500/30' 
                            : 'bg-green-500/20 text-green-400 border-green-500/30'
                        }`}>
                          {device.is_locked ? (
                            <Lock className="w-3 h-3" />
                          ) : (
                            <Unlock className="w-3 h-3" />
                          )}
                          <span>{device.is_locked ? 'Locked' : 'Unlocked'}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleLockRequest(device.id, device.is_locked ? 'UNLOCK' : 'LOCK')}
                          className={`px-3 py-1 rounded-lg text-xs font-medium transition-all duration-200 shadow-lg hover:shadow-xl ${
                            device.is_locked
                              ? 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white'
                              : 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white'
                          }`}
                        >
                          Request {device.is_locked ? 'Unlock' : 'Lock'}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Request History */}
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl border border-slate-700/50 shadow-2xl">
          <div className="px-6 py-4 border-b border-slate-700/30">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-violet-600 rounded-lg flex items-center justify-center">
                <Eye className="w-4 h-4 text-white" />
              </div>
              <h2 className="text-lg font-semibold text-white">My Request History</h2>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-slate-700/30">
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Device
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Request
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Reason
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/30">
                {requestsLoading ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center">
                      <div className="flex items-center justify-center space-x-2">
                        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                        <span className="text-slate-400">Loading requests...</span>
                      </div>
                    </td>
                  </tr>
                ) : lockRequests.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center text-slate-400">
                      No requests found
                    </td>
                  </tr>
                ) : (
                  lockRequests.map((request) => (
                    <tr key={request.id} className="hover:bg-white/5 transition-colors duration-150">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-white">
                          {request.device?.device_name}
                        </div>
                        <div className="text-sm text-slate-400">
                          {request.device?.serial_number}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full border text-xs font-medium ${
                          request.request_type === 'LOCK'
                            ? 'bg-red-500/20 text-red-400 border-red-500/30'
                            : 'bg-green-500/20 text-green-400 border-green-500/30'
                        }`}>
                          {request.request_type === 'LOCK' ? (
                            <Lock className="w-3 h-3" />
                          ) : (
                            <Unlock className="w-3 h-3" />
                          )}
                          <span>{request.request_type}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-white max-w-xs">
                        <div className="truncate" title={request.reason}>
                          {request.reason}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(request.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-400">
                        {new Date(request.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Simple Device View Modal */}
      {showDeviceModal && selectedDevice && (
        <div className="modal-overlay">
          <div className="bg-white/10 backdrop-blur-xl rounded-2xl shadow-2xl max-w-md w-full border border-slate-700/50">
            <div className="px-6 py-4 border-b border-slate-700/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                    <Smartphone className="w-4 h-4 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-white">Device Details</h3>
                </div>
                <button
                  onClick={() => {
                    setShowDeviceModal(false);
                    setSelectedDevice(null);
                  }}
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>
            <div className="px-6 py-6">
              {(() => {
                const device = devices.find(d => d.id === selectedDevice);
                if (!device) return <p className="text-slate-400">Device not found</p>;
                return (
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-slate-400">Device Name</label>
                      <p className="text-sm text-white mt-1">{device.device_name}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-400">Serial Number</label>
                      <p className="text-sm text-white mt-1 font-mono">{device.serial_number}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-400">Owner</label>
                      <p className="text-sm text-white mt-1">{device.owner_name}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-400">Status</label>
                      <div className="mt-1">
                        <div className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full border text-xs font-medium ${
                          device.is_locked 
                            ? 'bg-red-500/20 text-red-400 border-red-500/30' 
                            : 'bg-green-500/20 text-green-400 border-green-500/30'
                        }`}>
                          {device.is_locked ? (
                            <Lock className="w-3 h-3" />
                          ) : (
                            <Unlock className="w-3 h-3" />
                          )}
                          <span>{device.is_locked ? 'Locked' : 'Unlocked'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Lock Request Modal */}
      {showLockRequestModal && (
        <div className="modal-overlay">
          <div className="bg-white/10 backdrop-blur-xl rounded-2xl shadow-2xl max-w-md w-full border border-slate-700/50">
            <div className="px-6 py-4 border-b border-slate-700/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    requestType === 'LOCK' 
                      ? 'bg-gradient-to-br from-red-500 to-red-600' 
                      : 'bg-gradient-to-br from-green-500 to-emerald-600'
                  }`}>
                    {requestType === 'LOCK' ? (
                      <Lock className="w-4 h-4 text-white" />
                    ) : (
                      <Unlock className="w-4 h-4 text-white" />
                    )}
                  </div>
                  <h3 className="text-lg font-semibold text-white">
                    Request {requestType === 'LOCK' ? 'Lock' : 'Unlock'} Device
                  </h3>
                </div>
                <button
                  onClick={() => {
                    setShowLockRequestModal(false);
                    setRequestReason('');
                    setSelectedDevice(null);
                  }}
                  className="text-slate-400 hover:text-white transition-colors"
                  disabled={isSubmitting}
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-slate-300 mb-4 leading-relaxed">
                You are requesting to {requestType.toLowerCase()} this device. Please provide a reason for this request.
              </p>
              <textarea
                value={requestReason}
                onChange={(e) => setRequestReason(e.target.value)}
                placeholder="Enter reason for this request..."
                className="w-full px-4 py-3 bg-white/10 border border-slate-600/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 backdrop-blur-sm resize-none"
                rows={3}
                disabled={isSubmitting}
              />
            </div>
            <div className="px-6 py-4 border-t border-slate-700/30 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowLockRequestModal(false);
                  setRequestReason('');
                  setSelectedDevice(null);
                }}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-slate-300 bg-white/10 border border-slate-600/50 rounded-lg hover:bg-white/20 hover:border-slate-500/50 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={submitLockRequest}
                disabled={!requestReason.trim() || isSubmitting}
                className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 ${
                  requestType === 'LOCK'
                    ? 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800'
                    : 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700'
                }`}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Submitting...</span>
                  </>
                ) : (
                  <span>Submit Request</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinanceDashboard;
